import React, { Fragment } from 'react';
import { Mutation } from 'react-apollo';
import { connect } from 'react-redux';
import { css } from 'react-emotion';
import { get } from 'lodash';
import { components, fonts, colors } from '@peachjar/components';
import { Redirect } from 'react-router'
import config from '../../../../../../config';

import LearnMoreModal from '../components/modals/LearnMoreModal';
import AssignDocusignModal from '../components/modals/AssignDocusignModal';
import AssignPaymentModal from '../components/modals/AssignPaymentModal';
import StripeElementsWrapper from '../components/StripeElementsWrapper';
import StripeForm from '../components/StripeForm';
import PaymentIcon from '../components/Payment.svg';
import OrderSummary from '../components/OrderSummary';
import Footer from '../components/Footer';
import StripeButton from '../components/StripeButton';
import HeadingText from '../components/HeadingText';
import SigningIcon from '../components/Signing.svg';
import HorizontalRule from '../components/HorizontalRule';
import TaskSection from '../components/TaskSection';

import PAY_BY_CHECK_MUTATION from '../gql/paySchoolOrDistrictRegistrationByCheck.mutation';

const { platinum } = colors;
const { Spinner, SpinnerLarge } = components.LoadingIndicators;
const {
  ButtonPrimaryLarge,
  ButtonSecondarySmall,
  ButtonFlatSmall,
} = components.Buttons;

const styles = {
  grayBox: css({
    padding: '0.5rem',
    borderRadius: '0.25rem',
    backgroundColor: platinum,
  }),
};

const taskSection1 = {
  header: 'Signing',
  title: 'Peachjar Order Authorization',
  description: (
    <div style={{ display: 'inline-block', marginBottom: '1rem' }}>
      Please review and sign the following agreement. You can also assign it to
      an authorized signer.
    </div>
  ),
  completionTask: 'Agreement',
  completionText: 'signed',
};

const taskSection2 = {
  header: 'Payment',
  title: 'Payment Invoice',
  description: (
    <div>
      <p>
        Pay below or assign the payment invoice to an authorized payer. All
        credit and debit card transactions are secure and encrypted.
      </p>
      <span>View or assign invoice:</span>
    </div>
  ),
  hasLink: false,
  completionTask: 'Payment',
  completionText: 'complete',
};

/* if registrationId in the state is detected, we know to fetch the registraiton Info with registrationId */
export const PaymentContainerContents = ({
  didWriteRegistrationIdToRedux,
  paymentAssigneeName,
  isPaymentAssigneePresent,
  isAgreementAssigneePresent,
  event,
  token,
  showModal,
  closeModal,
  onLoadRegistrationId,
  handleState,
  handleContinue,
  openAccordion,
  whichAccordionIsOpen,
  loading,
  error,
  data,
}) => {
  let radioId = 0;
  if (loading) {
    return (
      <div className="layout-row flex-auto layout-align-center-center">
        <SpinnerLarge />
      </div>
    );
  }

  if (error) {
    return <div>Error</div>;
  }

  // the empty object at the end of this is to protect us from erroring
  // in the event of lag during our fetch
  const schoolOrDistrictData =
    get(data, 'lookupRegistrationByUser[0]') ||
    get(data, 'schoolOrDistrictRegistration', {
      pricingInfo: {},
      agreementAssignee: {},
      paymentAssignee: {},
    });

  const {
    numberOfSchools,
    discountCode,
    peachjarProduct = 'Peachjar Flyer Service',
    agreementStatus,
    paymentStatus,
    id: registrationId,
    canSetup,
    agreementId,
  } = schoolOrDistrictData;

  const {
    discountRate,
    startUpFeePerSchool,
    totalAmount,
    prediscountAmount,
  } = schoolOrDistrictData.pricingInfo;

  // Move to next page if Sign/Pay waived
  if (agreementStatus === 'waived' && paymentStatus === 'waived') {
    return <Redirect to="/onboarding/setup/school" />
  }

  // this is a little bit of a hack so that we can ensure
  // the registrationId gets written to redux for the following pages
  // when the user loads the registration via magic link token.
  if (!didWriteRegistrationIdToRedux && registrationId) {
    onLoadRegistrationId(registrationId);
    handleState('didWriteRegistrationIdToRedux', true);
  }

  const areWaitingForCheck = paymentStatus === 'wait-for-check';
  const hasPaid = paymentStatus === 'paid';

  return (
    <Fragment>
      <div className="container flex-none">
        <HeadingText
          paymentAssigneeName={paymentAssigneeName}
          isPaymentAssigneePresent={isPaymentAssigneePresent}
        />

        {isPaymentAssigneePresent ? null : (
          <Fragment>
            <div className="layout-row">
              <div className="layout-row layout-align-start-start flex-10">
                <SigningIcon />
              </div>

              <div className="flex-90">
                <TaskSection
                  header={taskSection1.header}
                  description={taskSection1.description}
                  title={taskSection1.title}
                  assignedStatus={taskSection1.status}
                  assignee={schoolOrDistrictData.agreementAssignee}
                  isAssigneePresent={isAgreementAssigneePresent}
                  showLearnMoreModal={() =>
                    showModal(LearnMoreModal, { closeModal })
                  }
                  completionTask={taskSection1.completionTask}
                  completionText={taskSection1.completionText}
                  completionStatus={agreementStatus}
                  event={event}
                >
                  <ButtonFlatSmall
                    data-testid="click-assignDocusignModalButton"
                    onClick={() =>
                      showModal(AssignDocusignModal, {
                        closeModal,
                        registrationId,
                      })
                    }
                  >
                    Assign
                  </ButtonFlatSmall>
                  <a
                    href={`${config.REACT_APP_API_BASE_URL}/districts/onboarding/${registrationId}/agreement${
                      token ? `/${token}` : ''
                    }`}
                    className="ml-2"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <ButtonSecondarySmall data-testid="click-signAgreementButton">
                      Review & Sign
                    </ButtonSecondarySmall>
                  </a>
                </TaskSection>

                {agreementId && event !== 'signing_complete' ? (
                  <div className={`pj-font-size-02 ${styles.grayBox}`}>
                    <span className="pj-font-weight-bold pj-font-family-thick">
                      Note:
                    </span>{' '}
                    <span>
                      This section will update once Docusign lets us know that
                      the agreement has been signed.
                    </span>
                  </div>
                ) : null}
              </div>
            </div>

            <HorizontalRule margin="3rem 0" />
          </Fragment>
        )}

        {isAgreementAssigneePresent ? null : (
          <Fragment>
            <div className="layout-row">
              <div className="layout-row layout-align-start-start flex-10">
                <PaymentIcon />
              </div>

              <div className="flex-90">
                <TaskSection
                  header={
                    areWaitingForCheck
                      ? 'Payment in Progress'
                      : taskSection2.header
                  }
                  description={taskSection2.description}
                  title={taskSection2.title}
                  assignedStatus={taskSection2.status}
                  hasLink={taskSection2.hasLink}
                  assignee={schoolOrDistrictData.paymentAssignee}
                  isAssigneePresent={isPaymentAssigneePresent}
                  completionTask={taskSection2.completionTask}
                  completionText={taskSection2.completionText}
                  completionStatus={paymentStatus}
                >
                  {isPaymentAssigneePresent ? null : (
                    <ButtonFlatSmall
                      data-testid="click-assignPaymentModalButton"
                      onClick={() =>
                        showModal(AssignPaymentModal, {
                          closeModal,
                          registrationId,
                        })
                      }
                    >
                      Assign
                    </ButtonFlatSmall>
                  )}
                  <a
                    href={`${config.REACT_APP_API_BASE_URL}/districts/onboarding/${registrationId}/downloads/invoice`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="ml-2"
                  >
                    <ButtonSecondarySmall
                      data-testid="click-viewInvoiceButton"
                      className="pj-margin-left--narrrow"
                    >
                      View
                    </ButtonSecondarySmall>
                  </a>
                </TaskSection>

                {hasPaid || areWaitingForCheck ? null : (
                  <div className="layout-row pj-margin-bottom--base">
                    <div className="layout-column flex-60">
                      <Mutation mutation={PAY_BY_CHECK_MUTATION}>
                        {(markPayingByCheck, { data, loading, error }) => {
                          if (error) {
                            return <div>Error</div>;
                          }

                          return (
                            <StripeButton
                              openAccordion={openAccordion}
                              whichAccordionIsOpen={whichAccordionIsOpen}
                              radioId={++radioId}
                              label="Check"
                            >
                              <div>
                                <HorizontalRule margin="1rem 0" />

                                <div>
                                  <span
                                    style={{
                                      fontFamily: fonts.ProximaNovaA,
                                      fontWeight: 'bold',
                                    }}
                                  >
                                    Send a check to:
                                  </span>

                                  <div
                                    style={{
                                      fontSize: fonts.pjFontSize03,
                                      marginTop: '1rem',
                                    }}
                                  >
                                    <div>
                                      Peachjar<br />
                                      13290 Evening Creek Dr #200<br />
                                      San Diego, CA 92128
                                    </div>
                                    <br />

                                    <span>
                                      Please send payment within 30 days.
                                    </span>
                                  </div>

                                  <div className="layout-row layout-align-end-center">
                                    <ButtonPrimaryLarge
                                      data-testid="submit-payByCheck"
                                      disabled={loading}
                                      onClick={() =>
                                        markPayingByCheck({
                                          variables: {
                                            registrationId,
                                          },
                                        })
                                      }
                                    >
                                      {loading && <Spinner />}
                                      Pay
                                    </ButtonPrimaryLarge>
                                  </div>
                                </div>
                              </div>
                            </StripeButton>
                          );
                        }}
                      </Mutation>

                      <StripeButton
                        openAccordion={openAccordion}
                        whichAccordionIsOpen={whichAccordionIsOpen}
                        radioId={++radioId}
                        label="Credit / Debit Card"
                      >
                        <div>
                          <HorizontalRule margin="0.5rem 0 1rem 0" />

                          <StripeElementsWrapper>
                            <StripeForm
                              schoolOrDistrictData={schoolOrDistrictData}
                            />
                          </StripeElementsWrapper>
                        </div>
                      </StripeButton>
                    </div>

                    <OrderSummary
                      numberOfSchools={numberOfSchools}
                      peachjarProduct={peachjarProduct}
                      discountCode={discountCode}
                      discountRate={discountRate}
                      startUpFeePerSchool={startUpFeePerSchool}
                      prediscountAmount={prediscountAmount}
                      totalAmount={totalAmount}
                      className="flex-40 layout-row"
                    />
                  </div>
                )}
              </div>
            </div>

            <HorizontalRule margin="3rem 0" />
          </Fragment>
        )}

        {!isPaymentAssigneePresent &&
          !isAgreementAssigneePresent && (
            <div className="layout-row layout-align-end-center">
              <ButtonPrimaryLarge
                data-testid="click-completeSigningAndPaymentButton"
                disabled={!canSetup}
                onClick={handleContinue}
              >
                Complete
              </ButtonPrimaryLarge>
            </div>
          )}
      </div>

      <Footer />
    </Fragment>
  );
};

const mapDispatchToProps = dispatch => ({
  onLoadRegistrationId: registrationId => {
    dispatch({
      type: 'auth/RECEIVE_REGISTRATION_ID',
      data: {
        registrationId,
      },
    });
  },
});

export default connect(
  null,
  mapDispatchToProps
)(PaymentContainerContents);



// WEBPACK FOOTER //
// ./src/Onboarding/Registration/onboarding/containers/Payment/containers/PaymentContainerContents.jsx